import React from "react";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import { Mic, MicOff, Video, VideoOff, Crown, Hand, Sparkles, Star, Shield } from "lucide-react";
import { ArenaHostControls } from "./ArenaHostControls";
import { cn } from "@/lib/utils";

interface ArenaParticipantsGridProps {
  participants: ArenaParticipant[];
  currentUserId?: string;
  onParticipantClick?: (participant: ArenaParticipant) => void;
  isHost?: boolean;
  hasModPrivileges?: boolean; // Host or co-host
  spotlightedUserId?: string;
  onSpotlightUser?: (userId: string) => void;
  cohostUserIds?: string[]; // List of co-host user IDs
}

const ArenaParticipantsGrid: React.FC<ArenaParticipantsGridProps> = ({
  participants,
  currentUserId,
  onParticipantClick,
  isHost = false,
  hasModPrivileges = false,
  spotlightedUserId,
  onSpotlightUser,
  cohostUserIds = [],
}) => {
  // Sort: speakers first, then by join time
  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.is_speaker !== b.is_speaker) return a.is_speaker ? -1 : 1;
    return new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
  });

  const speakers = sortedParticipants.filter(p => p.is_speaker);
  const listeners = sortedParticipants.filter(p => !p.is_speaker);

  const handleMuteParticipant = (userId: string) => {
    console.log("Mute participant:", userId);
  };

  const handleRemoveParticipant = (userId: string) => {
    console.log("Remove participant:", userId);
  };

  const handleSpotlightUser = (userId: string) => {
    onSpotlightUser?.(userId);
  };

  const handleReport = (userId: string) => {
    console.log("Report user:", userId);
  };

  const handleBlock = (userId: string) => {
    console.log("Block user:", userId);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Speakers section */}
      {speakers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
            <Crown className="w-4 h-4 text-amber-500" />
            Speakers
          </h4>
          <div className="grid grid-cols-3 gap-3">
            {speakers.map((participant) => (
              <ParticipantAvatar 
                key={participant.id} 
                participant={participant} 
                isCurrentUser={participant.user_id === currentUserId}
                onClick={() => onParticipantClick?.(participant)}
                hasModPrivileges={hasModPrivileges}
                isSpotlighted={participant.user_id === spotlightedUserId}
                isCohost={cohostUserIds.includes(participant.user_id)}
                onMuteParticipant={handleMuteParticipant}
                onRemoveParticipant={handleRemoveParticipant}
                onSpotlightUser={handleSpotlightUser}
                onReport={handleReport}
                onBlock={handleBlock}
              />
            ))}
          </div>
        </div>
      )}

      {/* Listeners section */}
      {listeners.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
            <Hand className="w-4 h-4" />
            Listeners ({listeners.length})
          </h4>
          <div className="grid grid-cols-4 gap-2">
            {listeners.map((participant) => (
              <ParticipantAvatar 
                key={participant.id} 
                participant={participant} 
                isCurrentUser={participant.user_id === currentUserId}
                compact
                onClick={() => onParticipantClick?.(participant)}
                hasModPrivileges={hasModPrivileges}
                isSpotlighted={participant.user_id === spotlightedUserId}
                isCohost={cohostUserIds.includes(participant.user_id)}
                onMuteParticipant={handleMuteParticipant}
                onRemoveParticipant={handleRemoveParticipant}
                onSpotlightUser={handleSpotlightUser}
                onReport={handleReport}
                onBlock={handleBlock}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {participants.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No one else here yet</p>
          <p className="text-sm">Be the first to speak up!</p>
        </div>
      )}
    </div>
  );
};

interface ParticipantAvatarProps {
  participant: ArenaParticipant;
  isCurrentUser: boolean;
  compact?: boolean;
  onClick?: () => void;
  hasModPrivileges?: boolean;
  isSpotlighted?: boolean;
  isCohost?: boolean;
  onMuteParticipant: (userId: string) => void;
  onRemoveParticipant: (userId: string) => void;
  onSpotlightUser: (userId: string) => void;
  onReport: (userId: string) => void;
  onBlock: (userId: string) => void;
}

const ParticipantAvatar: React.FC<ParticipantAvatarProps> = ({
  participant,
  isCurrentUser,
  compact = false,
  onClick,
  hasModPrivileges = false,
  isSpotlighted = false,
  isCohost = false,
  onMuteParticipant,
  onRemoveParticipant,
  onSpotlightUser,
  onReport,
  onBlock,
}) => {
  const size = compact ? "w-14 h-14" : "w-20 h-20";
  const imgSize = compact ? "w-12 h-12" : "w-16 h-16";

  return (
    <div 
      className={cn(
        "flex flex-col items-center gap-1 group relative",
        !isCurrentUser && onClick && "cursor-pointer"
      )}
      onClick={() => !isCurrentUser && onClick?.()}
    >
      <div className={`relative ${size} flex items-center justify-center`}>
        {/* Spotlight indicator */}
        {isSpotlighted && (
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-accent animate-pulse scale-125 opacity-30" />
        )}

        {/* Speaking indicator ring */}
        {!participant.is_muted && (
          <div className="absolute inset-0 rounded-full border-2 border-green-500 animate-pulse" />
        )}

        {/* Hover effect */}
        {!isCurrentUser && (
          <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors z-10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}

        {/* Spotlight badge */}
        {isSpotlighted && (
          <div className="absolute -top-1 -left-1 z-20">
            <Star className="w-4 h-4 text-primary fill-primary" />
          </div>
        )}

        {/* Avatar */}
        <img
          src={participant.profile?.photo_url || "https://api.dicebear.com/7.x/avataaars/svg?seed=default"}
          alt={participant.profile?.name || "Participant"}
          className={cn(
            imgSize, 
            "rounded-full object-cover border-2 transition-transform group-hover:scale-105",
            isCurrentUser ? "border-primary" : isSpotlighted ? "border-primary" : "border-white"
          )}
        />

        {/* Host controls - visible to moderators */}
        {!isCurrentUser && !compact && (
          <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <ArenaHostControls
              isHost={hasModPrivileges}
              participant={participant}
              onMuteParticipant={onMuteParticipant}
              onRemoveParticipant={onRemoveParticipant}
              onSpotlightUser={onSpotlightUser}
              onReport={onReport}
              onBlock={onBlock}
            />
          </div>
        )}

        {/* Co-host badge */}
        {isCohost && (
          <div className="absolute -top-1 -left-1 z-20 bg-blue-500 rounded-full p-0.5">
            <Shield className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Mic/Video indicators */}
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
          <div className={cn(
            "p-1 rounded-full transition-colors",
            participant.is_muted ? "bg-red-500" : "bg-green-500"
          )}>
            {participant.is_muted ? (
              <MicOff className="w-2.5 h-2.5 text-white" />
            ) : (
              <Mic className="w-2.5 h-2.5 text-white" />
            )}
          </div>
          {participant.is_video_on !== undefined && (
            <div className={cn(
              "p-1 rounded-full transition-colors",
              participant.is_video_on ? "bg-green-500" : "bg-muted"
            )}>
              {participant.is_video_on ? (
                <Video className="w-2.5 h-2.5 text-white" />
              ) : (
                <VideoOff className="w-2.5 h-2.5 text-muted-foreground" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Name */}
      {!compact && (
        <span className="text-xs font-medium text-foreground truncate max-w-full">
          {isCurrentUser ? "You" : participant.profile?.name || "Guest"}
        </span>
      )}
    </div>
  );
};

export default ArenaParticipantsGrid;
